import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, Users, ShieldCheck, Sparkles } from "lucide-react";
import DcPage from "@/components/landing/dcv2/DcPage";

const EASE = [0.16, 1, 0.3, 1] as const;

const VALUE_PROPS = [
  {
    icon: Briefcase,
    color: "var(--acc2)",
    title: "Curated opportunities",
    desc: "Roles matched to your skills, not noise.",
  },
  {
    icon: Users,
    color: "var(--acc2)",
    title: "A real network",
    desc: "Connect directly with employers and peers.",
  },
  {
    icon: ShieldCheck,
    color: "var(--pos)",
    title: "Private by design",
    desc: "Enterprise-grade encryption on every session.",
  },
];

/** Renders the panel heading with its final word in the electric gradient. */
function AccentHeading({ text }: { text: string }) {
  const words = text.trim().split(" ");
  const last = words.pop() ?? "";
  const lead = words.join(" ");
  return (
    <h2 className="text-4xl font-extrabold leading-[1.05] tracking-tight xl:text-5xl">
      {lead && (
        <span
          style={{
            color: "var(--landing-text)",
          }}
        >
          {lead}{" "}
        </span>
      )}
      <span
        style={{
          color: "var(--acc2)",
        }}
      >
        {last}
      </span>
    </h2>
  );
}

interface AuthLayoutProps {
  /** Large heading on the left brand panel (last word renders in the electric gradient). */
  panelHeading: string;
  /** Heading inside the form card. */
  heading: string;
  /** Subheading inside the form card. */
  subheading: string;
  /** Optional eyebrow pill above the form heading. */
  eyebrow?: string;
  /** Form body. */
  children: ReactNode;
}

export function AuthLayout({
  panelHeading,
  heading,
  subheading,
  eyebrow,
  children,
}: AuthLayoutProps) {
  return (
    <DcPage hideFooter>
      <section className="flex min-h-[calc(100vh-72px)]">
        {/* ─── Left panel: 55%, and it FILLS that 55% ───
            It used to be a 512px text block floating in a 1000px column, which
            is why the page read as unbalanced rather than as two columns: half
            the panel was nothing. The image is the panel now, the way the
            admin app's login does it, and the words sit on it.

            Hidden below lg rather than stacked. A decorative panel above a
            sign-in form on a phone is a screen of scrolling before the one
            control the visitor came for. */}
        <div
          className="relative hidden overflow-hidden lg:block lg:w-[55%]"
          style={{ borderRight: "1px solid rgba(var(--ink-rgb),.13)" }}
        >
          <div className="dc-auth-plate absolute inset-0">
            <img src="/plates/auth.jpg" alt="" aria-hidden="true" />
          </div>

          {/* The scrim is what makes the copy legible over whatever the image
              is doing underneath it, in either theme. It resolves from --paper,
              so it darkens in dark mode and lightens in light. */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(to top, var(--paper) 4%, color-mix(in srgb, var(--paper) 82%, transparent) 38%, color-mix(in srgb, var(--paper) 30%, transparent) 72%)",
            }}
          />

          <div className="relative flex h-full flex-col justify-end gap-7 p-14 xl:p-16">
            <div className="space-y-5">
              <AccentHeading text={panelHeading} />
              <p
                className="max-w-md text-base leading-relaxed"
                style={{ color: "rgba(var(--ink-rgb),.72)" }}
              >
                The intent engine for modern careers, where candidates,
                employers, and AI agents move as one.
              </p>
            </div>

            {/* A list, not three cards. They were same-size icon-heading-text
                tiles, which is the shape that makes any three things look like
                a feature grid; these are three sentences about one product. */}
            <ul className="flex flex-col gap-4 border-t pt-7" style={{ borderColor: "rgba(var(--ink-rgb),.16)" }}>
              {VALUE_PROPS.map((p) => (
                <li key={p.title} className="flex items-start gap-3.5">
                  <p.icon
                    className="mt-0.5 h-[18px] w-[18px] shrink-0"
                    style={{ color: "var(--acc2)" }}
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <p
                      className="text-sm font-semibold"
                      style={{ color: "var(--ink)" }}
                    >
                      {p.title}
                    </p>
                    <p
                      className="mt-0.5 text-[13px] leading-relaxed"
                      style={{ color: "rgba(var(--ink-rgb),.66)" }}
                    >
                      {p.desc}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ─── Right column: the form ─── */}
        <div className="flex flex-1 items-center justify-center p-6 sm:p-10 lg:p-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="w-full max-w-[420px]"
          >
            <div className="mb-6 space-y-3">
              {eyebrow && (
                <div
                  className="inline-flex items-center gap-2 rounded-md px-3 py-1.5"
                  style={{
                    background: "rgba(var(--ink-rgb),.06)",
                  }}
                >
                  <Sparkles
                    className="h-3.5 w-3.5"
                    style={{ color: "var(--acc2)" }}
                    aria-hidden="true"
                  />
                  <span
                    className="text-[11px] font-semibold uppercase tracking-widest"
                    style={{ color: "rgba(var(--ink-rgb),.72)" }}
                  >
                    {eyebrow}
                  </span>
                </div>
              )}
              <h1
                className="text-[1.75rem] font-extrabold leading-tight tracking-[-0.02em]"
                style={{ color: "var(--ink)" }}
              >
                {heading}
              </h1>
              <p
                className="text-sm leading-relaxed"
                style={{ color: "rgba(var(--ink-rgb),.66)" }}
              >
                {subheading}
              </p>
            </div>

            {/* No card. The form is the page on this side -- an enclosing panel
                inside a column that is already a panel is the nested-card shape
                the rest of the site does not use. */}
            {children}
          </motion.div>
        </div>
      </section>
    </DcPage>
  );
}
